import React from "react";
import ReactDOM from "react-dom";
import * as Icon from "react-feather";

import "./tools-select.css";

import WComponent from "../../../common/WComponent";

class ToolsSelect extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			isOpen: false,
			search: "",
			menuRect: null,
		};

		this.rootRef = React.createRef();
		this.menuRef = React.createRef();
	}

	componentWillUnmount() {
		this.detachListeners();
	}

	attachListeners = () => {
		document.addEventListener("mousedown", this.onDocumentMouseDown);
		window.addEventListener("resize", this.repositionMenu);
		window.addEventListener("scroll", this.repositionMenu, true);
	};

	detachListeners = () => {
		document.removeEventListener("mousedown", this.onDocumentMouseDown);
		window.removeEventListener("resize", this.repositionMenu);
		window.removeEventListener("scroll", this.repositionMenu, true);
	};

	onDocumentMouseDown = event => {
		const insideTrigger = this.rootRef.current && this.rootRef.current.contains(event.target);
		const insideMenu = this.menuRef.current && this.menuRef.current.contains(event.target);
		if (!insideTrigger && !insideMenu) {
			this.closeMenu();
		}
	};

	computeMenuRect = () => {
		const triggerElement = this.rootRef.current;
		if (!triggerElement) return null;

		const rect = triggerElement.getBoundingClientRect();
		return { top: rect.bottom + 6, left: rect.left, width: rect.width };
	};

	repositionMenu = () => {
		if (!this.state.isOpen) return;
		this.update({ menuRect: this.computeMenuRect() });
	};

	openMenu = async () => {
		this.attachListeners();
		await this.update({ isOpen: true, search: "", menuRect: this.computeMenuRect() });
	};

	closeMenu = async () => {
		this.detachListeners();
		await this.update({ isOpen: false, search: "" });
	};

	toggleMenu = () => {
		const { disabled } = this.props;
		if (disabled) return;

		if (this.state.isOpen) {
			this.closeMenu();
		} else {
			this.openMenu();
		}
	};

	onSelectOption = optionValue => {
		const { onChange } = this.props;
		this.closeMenu();
		onChange(optionValue);
	};

	onSearchKeyDown = event => {
		if (event.key === "Escape") {
			this.closeMenu();
		}
	};

	getSelectedOption = () => {
		const { value, options } = this.props;
		return options.find(option => option.value === value) || null;
	};

	getSelectedLabel = () => {
		const selectedOption = this.getSelectedOption();
		return selectedOption ? selectedOption.label : "";
	};

	getVisibleOptions = () => {
		const { options } = this.props;
		const { search } = this.state;

		const trimmedSearch = search.trim().toLowerCase();
		if (!trimmedSearch) return options;
		return options.filter(option => option.label.toLowerCase().includes(trimmedSearch));
	};

	renderOption = option => {
		const { value } = this.props;
		const isSelected = option.value === value;
		const optionClass = "tools-select__option" + (isSelected ? " tools-select__option--selected" : "");

		return (
			<button type="button" key={option.value} className={optionClass} onClick={() => this.onSelectOption(option.value)}>
				<span className="tools-select__option-label">{option.label}</span>
				{option.hint && <span className="tools-select__option-hint">{option.hint}</span>}
				{isSelected && <Icon.Check size={15} className="tools-select__option-check" />}
			</button>
		);
	};

	renderMenu = () => {
		const { searchable = false, searchPlaceholder = "Search…" } = this.props;
		const { search, menuRect } = this.state;
		if (!menuRect) return null;

		const visibleOptions = this.getVisibleOptions();
		const positionStyle = { top: menuRect.top, left: menuRect.left, width: menuRect.width };

		const menu = (
			<div className="tools-select__menu" style={positionStyle} ref={this.menuRef}>
				{searchable && (
					<div className="tools-select__search">
						<Icon.Search size={15} className="tools-select__search-icon" />
						<input
							className="tools-select__search-input"
							placeholder={searchPlaceholder}
							value={search}
							onChange={e => this.update({ search: e.target.value })}
							onKeyDown={this.onSearchKeyDown}
							autoFocus
						/>
					</div>
				)}
				<div className="tools-select__options">
					{visibleOptions.length === 0 && <div className="tools-select__empty">No matches</div>}
					{visibleOptions.map(this.renderOption)}
				</div>
			</div>
		);

		return ReactDOM.createPortal(menu, document.body);
	};

	render() {
		const { placeholder = "Select…", searchable = false, disabled, inline = false } = this.props;
		const { isOpen } = this.state;

		const selectedOption = this.getSelectedOption();
		const selectedLabel = this.getSelectedLabel();
		const hasValue = selectedLabel !== "";
		const rootClass = "tools-select" + (inline ? " tools-select--inline" : "");
		const triggerClass = "tools-select__trigger" + (isOpen ? " tools-select__trigger--open" : "");
		const valueClass = "tools-select__value" + (hasValue ? "" : " tools-select__value--placeholder");

		return (
			<div className={rootClass} ref={this.rootRef}>
				<button type="button" className={triggerClass} onClick={this.toggleMenu} disabled={disabled}>
					{searchable && <Icon.Search size={16} className="tools-select__lead-icon" />}
					<span className={valueClass}>{hasValue ? selectedLabel : placeholder}</span>
					{hasValue && selectedOption && selectedOption.hint && <span className="tools-select__value-hint">{selectedOption.hint}</span>}
					<Icon.ChevronDown size={18} className="tools-select__chevron" />
				</button>

				{isOpen && this.renderMenu()}
			</div>
		);
	}
}

export default ToolsSelect;
